import React from 'react';
import { TabPanel } from '../../../../../components/tabs';
import { IContactsTab } from './interfaces';
import { useTranslation } from 'react-i18next';
import { ManagedDataTable } from '../../../../../components/ManagedDataTable';
import { useGetContactRetentionOverview } from '../../../../../reactQuery/contacts';
import {
  ColumnFilters,
  IManagedDataTable,
} from '../../../../../components/ManagedDataTable/interfaces';
import { ContactRetentionEntry } from '../../../../../api/contactsApi/interfaces';
import { DATA_TABLE_STORAGE_KEYS } from '../../../../../constants';
import { getRowClassName } from '../../helpers';
import { getWarningRowStyling } from '../../../../../helpers/tableStyleHelper';
import { columns } from './helpers';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../../../../routes/Routes';

export const ContactsTab: React.FC<IContactsTab> = ({
  value,
  index,
  agentsList,
  contactsList,
  productsList,
  defaultFilter,
  isAdmin,
}): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const onRowClick = (row: ContactRetentionEntry): void => {
    navigate(ROUTES.contacts.details(row.id), { state: { from: location } });
  };

  const columnFilters: ColumnFilters = [
    {
      column: 'lastContactDate',
      label: t('clients:relationshipManagement.columns.lastContactDate'),
      type: 'date',
    },
    {
      column: 'dueDate',
      label: t('clients:relationshipManagement.columns.dueDate'),
      type: 'date',
    },
    {
      column: 'name',
      filterName: 'contacts',
      label: '???contacts',
      type: 'multivalue',
      options:
        contactsList?.map((contact) => {
          return {
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
          };
        }) || [],
    },
    {
      column: 'contractedProducts',
      label: t('entities:client.contractedProducts'),
      type: 'multivalue',
      options:
        productsList?.map((product) => {
          return {
            id: product.id,
            name: product.name,
          };
        }) || [],
    },
    {
      column: 'potentialProducts',
      label: t('entities:client.potentialProducts'),
      type: 'multivalue',
      options:
        productsList?.map((product) => {
          return {
            id: product.id,
            name: product.name,
          };
        }) || [],
    },
  ];

  if (isAdmin) {
    columnFilters.push({
      column: 'responsibleAgents',
      label: t('entities:client.responsibleAgents'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    });
  }

  return (
    <TabPanel value={value} index={index}>
      <ManagedDataTable
        query={
          useGetContactRetentionOverview as IManagedDataTable<ContactRetentionEntry>['query']
        }
        columns={columns}
        filter={defaultFilter}
        onRowClick={onRowClick}
        storageKey={DATA_TABLE_STORAGE_KEYS.contactRetentionOverview}
        initialSort={{ field: 'daysLeft', sort: 'asc' }}
        getRowClassName={getRowClassName}
        sx={getWarningRowStyling()}
        columnFilters={columnFilters}
      />
    </TabPanel>
  );
};
